/**
 * Validates that the given string is a well-formed http/https URL.
 */
export const isValidUrl = (str: string): boolean => {
  try {
    const url = new URL(str);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

/**
 * Returns the current camera permission state.
 *
 * Note: Firefox does not support querying the camera permission via the
 * Permissions API, so we fall back to 'prompt' (meaning: unknown, try anyway).
 * The actual browser prompt / denial will be caught by the scanner itself.
 */
export const getCameraPermissionState = async (): Promise<PermissionState> => {
  try {
    const result = await navigator?.permissions?.query?.({ name: 'camera' as PermissionName });
    return result.state;
  } catch {
    return 'prompt';
  }
};

/**
 * Returns true if the error thrown by getUserMedia / html5-qrcode indicates
 * the user explicitly denied camera access (NotAllowedError).
 */
export const isPermissionDeniedError = (err: unknown): boolean => {
  if (err instanceof DOMException) {
    return err.name === 'NotAllowedError';
  }
  return String(err).toLowerCase().includes('notallowed') || String(err).toLowerCase().includes('permission');
};

// ---------------------------------------------------------------------------
// Platform note:
// When native iOS/Android support is added via @capacitor/barcode-scanner,
// wrap the call with Capacitor.isNativePlatform() here and delegate to the
// native plugin. The QrScannerModal already checks this flag and will skip
// the web implementation on native platforms.
// ---------------------------------------------------------------------------
